import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { SuccessIcon, WarningIcon, DangerIcon, InfoIcon } from "../Icons";
import { animated, useSpring } from "@react-spring/web";
import i18n from "~/assets/js/Helpers/i18n";

const Toast = ({ id, variant, title, text, CTA, onCancel }) => {
    let icon;
    switch (variant) {
        case "success":
            icon = <SuccessIcon />;
            break;
        case "danger":
            icon = <DangerIcon />;
            break;
        case "info":
            icon = <InfoIcon />;
            break;
        case "warning":
            icon = <WarningIcon />;
            break;
        default:
            break;
    }

    const transition = useSpring({
        from: { x: 500, opacity: 0 },
        to: { x: 0, opacity: 1 },
    });

    const closeToast = () => onCancel(id);

    useEffect(() => {
        const FIVE_SECONDS = 5000;
        const timer = setTimeout(() => closeToast(), FIVE_SECONDS);
        return () => clearTimeout(timer);
    }, []);

    return (
        <animated.div style={transition} className="toast">
            <div className="toast__icon">{icon}</div>
            <div className="toast__content">
                <div className="toast__header">
                    <span className="toast__header-title">{title}</span>
                    <span className="toast__header-close" onClick={closeToast}>
                        <i className="fas fa-times"></i>
                    </span>
                </div>
                {text && <div className="toast__text">{text}</div>}
                {CTA && (
                    <div className="toast__CTA" onClick={CTA}>
                        <i className="fas fa-redo fa-flip-horizontal"></i>
                        {i18n.t("toasts.redo")}
                    </div>
                )}
            </div>
        </animated.div>
    );
};

const toastProp = {
    id: PropTypes.number,
    variant: PropTypes.oneOf(["success", "info", "danger", "warning"])
        .isRequired,
    title: PropTypes.string.isRequired,
    text: PropTypes.string,
    CTA: PropTypes.func,
    onCancel: PropTypes.func,
};

Toast.propTypes = toastProp;

export const ToastContainer = ({ toasts, onCancel }) => {
    return (
        <div className="toast__container">
            {toasts.map((toast) => (
                <Toast key={toast.id} {...toast} onCancel={onCancel} />
            ))}
        </div>
    );
};

ToastContainer.propTypes = {
    toasts: PropTypes.arrayOf(PropTypes.shape(toastProp)),
    onCancel: PropTypes.func,
};

/**
 * Helper function to dispatch a toast
 *
 * @param {string} variant
 * @param {string} title
 * @param {string} text
 * @param {func} CTA
 * @returns object
 */
export const AddToast = (variant, title, text = "", CTA) => {
    return { type: "ADD_TOAST", payload: { variant, title, text, CTA } };
};
